import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { DataOutResult } from '@/services/auth/types';
import { SHARED_QUERY_OPTIONS } from './constants';
import {
  filterDataOutResults,
  mapDataOutToSearchResult,
  mapMParticleToSearchResult,
  mapSegmentOutToSearchResult,
} from './utils/integration-results';
import { mergeQueryStates, type SearchSource } from './utils/source-bookkeeping';

type UseDataOutSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type DataOutSource = SearchSource<DataOutResult>;

export function useDataOutSource({ workspaceId, enabled, query }: UseDataOutSourceOptions): DataOutSource {
  const [mixpanelQuery, amplitudeQuery, segmentOutQuery, mparticleQuery] = useQueries({
    queries: [
      getServicesQueryOptions(
        'getDataOutMixpanel',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          throwOnError: false,
          retry: false,
          select: (data) => data.integration,
        },
      ),
      getServicesQueryOptions(
        'getDataOutAmplitude',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          throwOnError: false,
          retry: false,
          select: (data) => data.integration,
        },
      ),
      getServicesQueryOptions(
        'getDataOutSegmentOut',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          throwOnError: false,
          retry: false,
          select: (data) => data.segment_out,
        },
      ),
      getServicesQueryOptions(
        'getDataOutMParticle',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          throwOnError: false,
          retry: false,
          select: (data) => data.mparticle,
        },
      ),
    ],
  });

  const sourceState = mergeQueryStates([mixpanelQuery, amplitudeQuery, segmentOutQuery, mparticleQuery]);
  const results = useMemo(() => {
    const mappedResults = [
      mixpanelQuery.data != null ? mapDataOutToSearchResult(mixpanelQuery.data, workspaceId) : null,
      amplitudeQuery.data != null ? mapDataOutToSearchResult(amplitudeQuery.data, workspaceId) : null,
      segmentOutQuery.data != null ? mapSegmentOutToSearchResult(segmentOutQuery.data, workspaceId) : null,
      mparticleQuery.data != null ? mapMParticleToSearchResult(mparticleQuery.data, workspaceId) : null,
    ];

    return filterDataOutResults(
      mappedResults.filter((result): result is DataOutResult => result != null),
      query,
    );
  }, [mixpanelQuery.data, amplitudeQuery.data, segmentOutQuery.data, mparticleQuery.data, query, workspaceId]);

  return { ...sourceState, results };
}
